import { ReactNode, useMemo, useState } from 'react';

import { useStoreClosedActionSheet } from '@fhs-legacy/frontend/src/components/store-closed-action-sheet';
import { IRestaurantNode } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { useMessage } from '@fhs-legacy/frontend/src/pages/store-locator/new-ui/store-actions/hooks/use-message';
import { useStoreActionOptions } from '@fhs-legacy/frontend/src/pages/store-locator/new-ui/store-actions/hooks/use-store-action-options';
import { IActionOption } from '@fhs-legacy/frontend/src/pages/store-locator/new-ui/store-actions/types';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { StoreCardButtonOptions } from '@fhs-legacy/frontend/src/utils/launchdarkly/variations';
import { isCatering } from '@fhs-legacy/frontend/src/utils/service-mode';

interface StoreCardOrderAction {
  orderAction?: IActionOption;
  /** Reason the store can't be ordered from, if any. Doubles as the disabled trigger. */
  unavailableMessage?: string;
  isDisabled: boolean;
  /** Future-ordering confirmation sheet — the caller must render it. */
  storeClosedActionSheet: ReactNode;
  /** Set when the guest has a cart at another store and must confirm the switch. */
  showChangeToPickUpDialog: boolean;
  dismissChangeToPickUpDialog: () => void;
  confirmChangeToPickUp: () => void;
}

/**
 * The v2 card puts an order CTA on every row rather than only the selected one, but the
 * behaviour behind it — future-ordering confirmation, cart conflicts, loyalty offer
 * re-evaluation, catering — must stay identical to the legacy `StoreActions`, so this wraps
 * the same hooks and exposes just the Order option.
 */
export function useStoreCardOrderAction(restaurant: IRestaurantNode): StoreCardOrderAction {
  const { serviceMode } = useServiceModeContext();
  const unavailableMessage = useMessage({ restaurant });
  const [showChangeToPickUpDialog, setShowChangeToPickUpDialog] = useState(false);

  const { requestScheduleFutureOrderingConfirmation, storeClosedActionSheet } =
    useStoreClosedActionSheet({
      restaurant,
      isCatering: isCatering(serviceMode),
    });

  const actionOptions = useStoreActionOptions({
    restaurant,
    setShowChangeToPickUpDialog,
    showChangeToPickUpDialog,
    validateRestaurantSelection: requestScheduleFutureOrderingConfirmation,
  });

  const orderAction = useMemo(
    () => actionOptions.find(option => option.id === StoreCardButtonOptions.ORDER),
    [actionOptions]
  );

  return {
    orderAction,
    unavailableMessage,
    isDisabled: Boolean(orderAction?.disabled) && Boolean(unavailableMessage),
    storeClosedActionSheet,
    showChangeToPickUpDialog,
    dismissChangeToPickUpDialog: () => setShowChangeToPickUpDialog(false),
    confirmChangeToPickUp: () => orderAction?.onClick({ skipRestaurantValidation: true }),
  };
}
